/**
 * Grid configurations for the six reference tables shown under
 * Admin → Reference Data. Column keys are the snake_case DB column names the
 * backend returns; one AdminRefTable renders all of them.
 *
 * Column fields:
 *   key            DB column
 *   label          header text
 *   edit           'text' | 'number' | 'checkbox' | 'select' | 'typeSelect' | false
 *   readOnlyOnEdit key columns: editable when adding, read-only when editing
 *   upper          upper-case as the user types
 *   maxLength / required / options / help / mono / render(row) / sortable
 *
 * `keyColumns` build the upsert/delete key (composite keys are '|'-joined).
 */
import { getAccessToken } from '../utils/auth';
import { uploadAirlineLogo } from '../utils/httpApi';
import { isApiSuccess, getFirstApiError } from '../utils/api';
import { fileToBase64 } from '../utils/fileToBase64';
import { getAirlineLogo, loadAirlineLogoMap } from '../utils/airlineLogoMap';

const TRUE_VALUES = new Set(['1', 'true', 'TRUE', 'Y', 'yes']);

/** Backend booleans arrive as "0"/"1" strings; be tolerant of the obvious variants. */
export function toBool(v) {
  if (v === true || v === 1) return true;
  if (v == null) return false;
  return TRUE_VALUES.has(String(v));
}

const LOCKED_COLUMN = {
  key: 'locked', label: 'Locked', edit: 'checkbox',
  help: 'Locked rows are never overwritten by the ICAOList sync'
};

/** Own logo, else the aliased airline's logo from the runtime map. */
export function resolveAirlineLogoUrl(row) {
  if (row.logo_url) return row.logo_url;
  if (row.logo_alias_icao) return getAirlineLogo(row.logo_alias_icao) || null;
  return null;
}

const AIRLINES = {
  key: 'airlines',
  table: 'icao_airline',
  title: 'Airlines',
  keyColumns: ['icao_code'],
  defaultSort: { by: 'icao_code', dir: 'asc' },
  searchHint: 'Search ICAO, IATA, name or callsign',
  columns: [
    { key: 'icao_code', label: 'ICAO', edit: 'text', required: true, maxLength: 3, upper: true, readOnlyOnEdit: true, mono: true },
    { key: 'iata_code', label: 'IATA', edit: 'text', maxLength: 3, upper: true, mono: true },
    { key: 'airline_name', label: 'Name', edit: 'text', required: true, maxLength: 255 },
    { key: 'callsign', label: 'Callsign', edit: 'text', maxLength: 64 },
    { key: 'country', label: 'Country', edit: 'text', maxLength: 128 },
    { key: 'country_code', label: 'CC', edit: 'text', maxLength: 2, upper: true, mono: true },
    { key: 'is_military', label: 'Military', edit: 'checkbox' },
    { key: 'logo_alias_icao', label: 'Logo alias', edit: 'text', maxLength: 3, upper: true, mono: true,
      help: "Use this airline's logo instead of uploading one" },
    { key: 'logo', label: 'Logo', edit: false, sortable: false,
      render: (row) => {
        const url = resolveAirlineLogoUrl(row);
        const dark = row.logo_url_dark;
        return (
          <>
            {url ? <img className="admin-logo-preview" src={url} alt="" loading="lazy" /> : <span className="gap">—</span>}
            {dark && <img className="admin-logo-preview admin-logo-preview-dark" src={dark} alt="map variant" title="Map-label variant" loading="lazy" />}
          </>
        );
      } },
    LOCKED_COLUMN
  ],
  filters: [
    { key: 'missing_logo', label: 'Missing logo', filter: { missing_logo: '1' } },
    { key: 'has_alias', label: 'Has alias', filter: { has_alias: '1' } },
    { key: 'military', label: 'Military', filter: { is_military: '1' } }
  ],
  emptyForm: { icao_code: '', iata_code: '', airline_name: '', callsign: '', country: '', country_code: '', is_military: false, logo_alias_icao: '', locked: true },
  renderEditExtra: ({ extra, setExtra }) => (
    <>
      <label className="admin-edit-form-wide">
        Logo (PNG / JPG / SVG; PNG/JPG backgrounds auto-removed)
        <input type="file" accept="image/*" aria-label="Logo file"
          onChange={e => setExtra({ ...extra, logoFile: e.target.files && e.target.files[0] })} />
      </label>
      <label className="admin-edit-form-wide">
        Map-label logo (optional: a light version for the dark label chip on the live map)
        <input type="file" accept="image/*" aria-label="Map-label logo file"
          onChange={e => setExtra({ ...extra, logoFileDark: e.target.files && e.target.files[0] })} />
      </label>
    </>
  ),
  // Upload the logo(s) (if picked) after the row is saved, then refresh the
  // live map so tooltips / History pick them up without a reload.
  afterSave: async ({ values, extra }) => {
    const uploads = [
      [extra && extra.logoFile, null],
      [extra && extra.logoFileDark, 'dark']
    ];
    for (const [file, variant] of uploads) {
      if (!file) continue;
      const fileBase64 = await fileToBase64(file);
      const resp = await uploadAirlineLogo({
        icaoCode: values.icao_code,
        fileName: file.name,
        fileBase64,
        ...(variant ? { variant } : {})
      }, getAccessToken());
      if (!isApiSuccess(resp)) {
        throw new Error(getFirstApiError(resp) || (variant ? 'Map-label logo upload failed' : 'Logo upload failed'));
      }
    }
    loadAirlineLogoMap();
  },
  canAdd: true,
  canDelete: true
};

const AIRCRAFT_TYPES = {
  key: 'aircraftTypes',
  table: 'icao_aircraft_type',
  title: 'Aircraft Types',
  keyColumns: ['type_designator'],
  defaultSort: { by: 'type_designator', dir: 'asc' },
  searchHint: 'Search designator or manufacturer/model',
  columns: [
    { key: 'type_designator', label: 'Type', edit: 'text', required: true, maxLength: 4, upper: true, readOnlyOnEdit: true, mono: true },
    { key: 'manufacturer_model', label: 'Manufacturer / model', edit: 'text', required: true, maxLength: 255 },
    { key: 'aircraft_class', label: 'Class', edit: 'select',
      options: ['LandPlane', 'SeaPlane', 'Amphibian', 'Helicopter', 'Gyrocopter', 'Tiltrotor', 'Other'] },
    { key: 'engine_count', label: 'Engines', edit: 'number' },
    { key: 'engine_type', label: 'Engine type', edit: 'select', options: ['Jet', 'Turboprop/Turboshaft', 'Piston', 'Electric', 'Rocket', 'Other'] },
    { key: 'source', label: 'Source', edit: false },
    LOCKED_COLUMN
  ],
  emptyForm: { type_designator: '', manufacturer_model: '', aircraft_class: 'LandPlane', engine_count: '', engine_type: 'Jet', locked: true },
  canAdd: true,
  canDelete: true
};

const HEX_RANGES = {
  key: 'hexRanges',
  table: 'icao_country_range',
  title: 'Hex Ranges',
  keyColumns: ['hex_start', 'hex_end'],
  defaultSort: { by: 'hex_start', dir: 'asc' },
  searchHint: 'Search country or hex',
  columns: [
    { key: 'hex_start', label: 'Start', edit: 'text', required: true, maxLength: 6, upper: true, readOnlyOnEdit: true, mono: true },
    { key: 'hex_end', label: 'End', edit: 'text', required: true, maxLength: 6, upper: true, readOnlyOnEdit: true, mono: true },
    { key: 'country', label: 'Country', edit: 'text', required: true, maxLength: 128 },
    { key: 'country_code', label: 'CC', edit: 'text', maxLength: 2, upper: true, mono: true },
    { key: 'is_nested', label: 'Nested', edit: 'checkbox' },
    { key: 'source', label: 'Source', edit: false },
    LOCKED_COLUMN
  ],
  emptyForm: { hex_start: '', hex_end: '', country: '', country_code: '', is_nested: false, locked: true },
  canAdd: true,
  canDelete: true
};

const REGISTRY = {
  key: 'registry',
  table: 'icao_aircraft_registry',
  title: 'Registry',
  keyColumns: ['icao_hex'],
  defaultSort: { by: 'icao_hex', dir: 'asc' },
  requireQuery: true,
  searchHint: 'Type a hex or registration prefix to search',
  columns: [
    { key: 'icao_hex', label: 'Hex', edit: false, mono: true },
    { key: 'registration', label: 'Registration', edit: 'text', maxLength: 16, upper: true, mono: true },
    { key: 'icao_type_designator', label: 'Type', edit: 'typeSelect', mono: true },
    { key: 'manufacturer', label: 'Manufacturer', edit: 'text', maxLength: 128 },
    { key: 'model', label: 'Model', edit: 'text', maxLength: 128 },
    { key: 'owner_operator', label: 'Owner / operator', edit: 'text', maxLength: 255 },
    { key: 'is_military', label: 'Military', edit: 'checkbox' },
    LOCKED_COLUMN
  ],
  emptyForm: {},
  canAdd: false,
  canDelete: true,
  // deleteReferenceRow on the registry requires confirm:true in the request.
  deleteNeedsConfirmFlag: true
};

const REG_PREFIXES = {
  key: 'regPrefixes',
  table: 'icao_reg_prefix',
  title: 'Reg Prefixes',
  keyColumns: ['country', 'prefix'],
  defaultSort: { by: 'prefix', dir: 'asc' },
  searchHint: 'Search country or prefix',
  columns: [
    { key: 'prefix', label: 'Prefix', edit: 'text', required: true, maxLength: 8, upper: true, readOnlyOnEdit: true, mono: true },
    { key: 'country', label: 'Country', edit: 'text', required: true, maxLength: 128, readOnlyOnEdit: true },
    { key: 'note', label: 'Note', edit: 'text', maxLength: 255 },
    { key: 'source', label: 'Source', edit: false },
    LOCKED_COLUMN
  ],
  emptyForm: { prefix: '', country: '', note: '', locked: true },
  canAdd: true,
  canDelete: true
};

const TYPE_ALIASES = {
  key: 'typeAliases',
  table: 'icao_type_alias',
  title: 'Type Aliases',
  keyColumns: ['old_code'],
  defaultSort: { by: 'old_code', dir: 'asc' },
  searchHint: 'Search old or new code',
  columns: [
    { key: 'old_code', label: 'Old code', edit: 'text', required: true, maxLength: 4, upper: true, readOnlyOnEdit: true, mono: true },
    { key: 'new_code', label: 'New code', edit: 'typeSelect', required: true, mono: true },
    { key: 'source', label: 'Source', edit: false },
    LOCKED_COLUMN
  ],
  emptyForm: { old_code: '', new_code: '', locked: true },
  canAdd: true,
  canDelete: true
};

export const REF_DATA_CONFIGS = [AIRLINES, AIRCRAFT_TYPES, HEX_RANGES, REGISTRY, REG_PREFIXES, TYPE_ALIASES];

export default REF_DATA_CONFIGS;
